// FILE: src/Components/Copilot/CopilotHome.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
Dropdown,
Modal,
Select,
Spin,
message as antdMessage,
} from "antd";

import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CopilotHistoryDrawer from "./CopilotHistoryDrawer";
import CopilotMessageBubble from "./CopilotMessageBubble";
import CopilotGoalCard from "./CopilotGoalCard";
import {
  clearCopilotConversation,
  fetchCopilotMessagesThunk,
  fetchCopilotSessionsThunk,
  cancelCopilotActionThunk,
  cancelCopilotGoalThunk,
  cancelCopilotActionDraftThunk,
  confirmCopilotActionThunk,
  deleteCopilotSessionThunk,
editCopilotActionThunk,
patchCopilotActionDraftThunk,
renameCopilotSessionThunk,
sendCopilotChatThunk,
startCopilotReadResultActionThunk,
} from "../../store/Slices/CopilotSlice";
import {
fetchGoogleFonts,
uploadAssetService,
} from "../../services/assets";
import {
country as countryList,
} from "../../Comman Components/Country";
import {
getBoardApi,
getYoutubePlaylistsApi,
} from "../../services/publish";
import {
getOnboardingForGroup,
} from "../../services/group";
import ConnectProfileModal from "../ConnectProfileModal";
import {
fetchAllProfileDetails,
fetchGroupDetails,
fetchGroups,
setSelectedGroup,
} from "../../store/Slices/GroupSlice";
import {
  clearSocialConnectResult,
  getSocialConnectResult,
} from "../../utils/socialConnectOrigin";
import {
  recordCopilotAccountConnectResult,
} from "../../services/copilot";
import {
APP_TRANSACTION_TERMINAL_EVENT,
fetchImageGenerateModels,
} from "../../services/apps";

const MAX_COPILOT_CHAT_ATTACHMENTS = 8;


const modelOptions = [
  {
    label: "GPT 5.5",
    value: "gpt 5.5",
  },
  {
    label: "Grok 4.3",
    value: "grok 4.3",
  },
  {
    label: "Opus 4.8",
    value: "opus 4.8",
  },
];

const quickPrompts = [
  {
    title: "Create Post",
    icon: "fa-regular fa-pen-to-square",
    prompt: "Create a post for my selected brand.",
  },
  {
title: "Generate Carousel",
icon: "fa-solid fa-layer-group",
prompt: "Run Carousel Maker to generate a carousel for my next campaign.",
},
  {
    title: "Analyze Reports",
    icon: "fa-solid fa-chart-line",
    prompt: "Analyze my social performance for this month.",
  },
];

const skillPrompts = [
  {
    label: "Brand Kit",
    prompt: "Summarize my brand vibe from my Brand Kit.",
  },
  {
    label: "Posts",
    prompt: "Show me what posts are scheduled.",
  },
  {
    label: "Assets",
    prompt: "Find media assets for a premium campaign.",
  },
  {
    label: "Reports",
    prompt: "Analyze recent Instagram performance.",
  },
  {
    label: "Apps",
    prompt: "Suggest which AI app I should use next.",
  },
];

const getTimeGreeting = () => {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  if (hour >= 17 && hour < 22) return "Good evening";

  return "Working late";
};

const buildGreeting = (firstName, seed) => {
  const name = firstName || "there";
  const timeGreeting = getTimeGreeting();

  const greetings = [
    `Hey there, ${name}`,
    `${timeGreeting}, ${name}`,
    `What's the vibe, ${name}?`,
    `What should we improve today, ${name}?`,
    `What are we making today, ${name}?`,
  ];

  return greetings[seed % greetings.length];
};

const getSessionId = (session) => {
  return session?._id || session?.id || session?.sessionId || null;
};

const getKnowledgeStatusText = (knowledge) => {
  if (!knowledge?.enabled) return "Knowledge retrieval off";
  if (knowledge.error) return "Knowledge retrieval had an issue";

  if (knowledge.count > 0) {
    return `${knowledge.count} source${knowledge.count === 1 ? "" : "s"} used`;
  }

  return "No matching workspace knowledge found";
};

const SourceReferenceBar = ({ knowledge }) => {
  const refs = knowledge?.sourceReferences || [];

  if (!knowledge?.enabled) {
    return null;
  }

  return (
    <div className="mx-auto mt-3 flex max-w-3xl flex-wrap items-center justify-center gap-2">
      <span className="rounded-full border border-white/[0.1] bg-white/[0.04] px-3 py-1.5 text-xs font-bold text-white/45">
        <i className="fa-solid fa-database mr-1.5"></i>
        {getKnowledgeStatusText(knowledge)}
      </span>

      {refs.slice(0, 6).map((source) => (
        <span
          key={`${source.ref || source.sourceId}-${source.sourceType}`}
          title={source.title || source.sourceIdentity || source.sourceType}
          className="max-w-[180px] truncate rounded-full border border-white/[0.1] bg-white/[0.04] px-3 py-1.5 text-xs font-bold text-white/50"
        >
          {source.ref ? `[${source.ref}] ` : ""}
          {source.title || source.sourceType || "Source"}
        </span>
      ))}
    </div>
  );
};

const getUploadedAssetId = (uploadedAsset = {}) => {
  return (
    uploadedAsset.assetId ||
    uploadedAsset.libraryAsset?._id ||
    uploadedAsset.outputLibraryAssetId ||
    null
  );
};

const stripAttachmentForRequest = (attachment = {}) => {
  const { file, status, error, progress, ...requestAttachment } = attachment;
  return requestAttachment;
};

const getMessageRenderId = (message, fallback = "") => {
  return message?._id || message?.id || fallback;
};

const groupConsecutiveToolMessages = (messages = []) => {
  const groupedMessages = [];
  let pendingToolMessages = [];

  const flushToolMessages = (
    promptGoal = null
  ) => {
    if (pendingToolMessages.length === 0) {
      return false;
    }

    const firstToolMessage = pendingToolMessages[0];
    const lastToolMessage =
      pendingToolMessages[pendingToolMessages.length - 1];

    groupedMessages.push({
      id: `tool_group_${getMessageRenderId(
        firstToolMessage,
        "first"
      )}_${getMessageRenderId(lastToolMessage, "last")}`,
      role: "tool",
      toolMessages: [...pendingToolMessages],
      createdAt: lastToolMessage.createdAt || firstToolMessage.createdAt,
      promptGoal,
    });

    pendingToolMessages = [];
    return true;
  };

  messages.forEach((message) => {
    if (message?.role === "tool") {
      pendingToolMessages.push(message);
      return;
    }

    const promptGoal =
      message?.metadata?.goal ||
      null;
    const goalAttachedToTools =
      flushToolMessages(
        promptGoal
      );

    groupedMessages.push({
      ...message,
      promptGoal:
        goalAttachedToTools
        ? null
        : promptGoal,
    });
  });

  flushToolMessages();

  return groupedMessages;
};

export default function CopilotHome() {
const dispatch = useDispatch();
const location = useLocation();
const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const chatScrollRef = useRef(null);
  const chatWasNearBottomRef = useRef(true);

  const { selectedGroup, groups } = useSelector((state) => state.groups);
  const profile = useSelector((state) => state.Profiles?.[0]);
  const copilot = useSelector((state) => state.copilot);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState("gpt 5.5");
  const [inputValue, setInputValue] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [showSkills, setShowSkills] = useState(false);
  const [
    showScrollToBottom,
    setShowScrollToBottom,
  ] = useState(false);
const [
connectInteraction,
setConnectInteraction,
] = useState(null);

const socialConnectResultProcessingRef =
  useRef(false);

const [greetingSeed] = useState(() => Math.floor(Math.random() * 1000));

  const selectedGroupDetails = useMemo(() => {
    if (!selectedGroup || !Array.isArray(groups)) return null;
    return groups.find((group) => group.name === selectedGroup) || null;
  }, [groups, selectedGroup]);

  const selectedGroupId = selectedGroupDetails?._id || null;

  const greetingText = useMemo(() => {
    return buildGreeting(profile?.first_name, greetingSeed);
  }, [profile?.first_name, greetingSeed]);

  const hasConversation =
    copilot.messages.length > 0 ||
    Boolean(copilot.goal);
  const isThinking = copilot.chatStatus === "loading";
  const isActionRunning =
    copilot.actionStatus === "loading";

  const isActionDraftRunning =
    copilot.actionDraftStatus ===
    "loading";

  const isGoalRunning =
    copilot.goalStatus ===
    "loading";

  const hasUploadingAttachments = attachments.some(
    (attachment) => attachment.status === "uploading"
  );

  const isBusy =
    isThinking ||
    isActionRunning ||
    isActionDraftRunning ||
    hasUploadingAttachments;
  const historyLoading = copilot.sessionsStatus === "loading";
  const currentSessionId = copilot.currentSessionId;

  const renderedMessages = useMemo(() => {
    return groupConsecutiveToolMessages(copilot.messages);
  }, [copilot.messages]);

  useEffect(() => {
    const handleTerminalTransaction =
      (event) => {
        const eventSessionId =
          event?.detail
            ?.copilotSessionId ||
          null;

        if (
          !currentSessionId ||
          (
            eventSessionId &&
            String(
              eventSessionId
            ) !==
              String(
                currentSessionId
              )
          )
        ) {
          return;
        }

        dispatch(
          fetchCopilotMessagesThunk(
            currentSessionId
          )
        );

        if (selectedGroupId) {
          dispatch(
            fetchCopilotSessionsThunk({
              groupId:
                selectedGroupId,
              page: 1,
              limit: 20,
            })
          );
        }
      };

    window.addEventListener(
      APP_TRANSACTION_TERMINAL_EVENT,
      handleTerminalTransaction
    );

    return () => {
      window.removeEventListener(
        APP_TRANSACTION_TERMINAL_EVENT,
        handleTerminalTransaction
      );
    };
  }, [
    currentSessionId,
    dispatch,
    selectedGroupId,
  ]);

  const updateChatScrollPosition = () => {
    const container =
      chatScrollRef.current;

    if (!container) {
      return;
    }

    const distanceFromBottom =
      container.scrollHeight -
      container.scrollTop -
      container.clientHeight;
    const isNearBottom =
      distanceFromBottom <=
      120;

    chatWasNearBottomRef.current =
      isNearBottom;
    setShowScrollToBottom(
      !isNearBottom
    );
  };

  const scrollChatToBottom = (
    behavior = "smooth"
  ) => {
    const container =
      chatScrollRef.current;

    if (!container) {
      return;
    }

    container.scrollTo({
      top:
        container.scrollHeight,
      behavior,
    });

    chatWasNearBottomRef.current =
      true;
    setShowScrollToBottom(
      false
    );
  };

  useEffect(() => {
    const container =
      chatScrollRef.current;

    if (!container) {
      return;
    }

    if (
      chatWasNearBottomRef.current ||
      isThinking ||
      isActionRunning ||
      isActionDraftRunning
    ) {
      window.requestAnimationFrame(
        () => {
          scrollChatToBottom(
            "smooth"
          );
        }
      );
    }
  }, [
    renderedMessages.length,
    isThinking,
    isActionRunning,
    isActionDraftRunning,
  ]);

  const latestClarificationMessageId =
    useMemo(() => {
      const messages =
        Array.isArray(
          copilot.messages
        )
          ? copilot.messages
          : [];

      for (
        let index =
          messages.length - 1;
        index >= 0;
        index -= 1
      ) {
        const message =
          messages[index];

        if (
          message?.role !==
          "assistant"
        ) {
          continue;
        }

        const interactionType =
          message?.metadata
            ?.clarificationInteraction
            ?.type;

        if (
  [
    "action_form",
    "account_connect",
  ].includes(
    interactionType
  )
) {
  return getMessageRenderId(
    message,
    ""
  );
}

        return null;
      }

      return null;
    }, [copilot.messages]);


    useEffect(() => {
  const params =
    new URLSearchParams(
      location.search
    );

  const requestedSessionId =
    params.get(
      "copilotSession"
    );

  const connectionResult =
    getSocialConnectResult();

  if (
    connectionResult &&
    !socialConnectResultProcessingRef
      .current
  ) {
    const targetSessionId =
      connectionResult.sessionId ||
      requestedSessionId ||
      null;

    if (!targetSessionId) {
      clearSocialConnectResult();

      navigate(
        "/Dashboard",
        {
          replace: true,
        }
      );

      return;
    }

    socialConnectResultProcessingRef.current =
      true;

    const persistResult =
      async () => {
        try {
          await recordCopilotAccountConnectResult({
  sessionId:
    targetSessionId,
  resultId:
    connectionResult.resultId,
  platform:
    connectionResult.platform ||
    null,
  requestedPlatforms:
    connectionResult
      .requestedPlatforms ||
    [],
  remainingPlatforms:
    connectionResult
      .remainingPlatforms ||
    [],
  accounts:
    connectionResult.accounts ||
    [],
});

          clearSocialConnectResult();

          await dispatch(
            fetchCopilotMessagesThunk(
              targetSessionId
            )
          );

          if (selectedGroupId) {
            dispatch(
              fetchCopilotSessionsThunk({
                groupId:
                  selectedGroupId,
                page: 1,
                limit: 20,
              })
            );
          }

          navigate(
            "/Dashboard",
            {
              replace: true,
            }
          );
        } catch (error) {
          antdMessage.error(
            error.message ||
              "The profile was connected, but Copilot could not save the connection result."
          );

          socialConnectResultProcessingRef.current =
            false;
        }
      };

    persistResult();
    return;
  }

  if (
    requestedSessionId &&
    String(
      currentSessionId || ""
    ) !==
      String(
        requestedSessionId
      )
  ) {
    dispatch(
      fetchCopilotMessagesThunk(
        requestedSessionId
      )
    ).finally(() => {
      navigate(
        "/Dashboard",
        {
          replace: true,
        }
      );
    });

    return;
  }

  if (requestedSessionId) {
    navigate(
      "/Dashboard",
      {
        replace: true,
      }
    );
  }
}, [
  dispatch,
  location.search,
  navigate,
  currentSessionId,
  selectedGroupId,
]);
  useEffect(() => {
    if (!selectedGroupId) return;

    dispatch(
      fetchCopilotSessionsThunk({
        groupId: selectedGroupId,
        page: 1,
        limit: 20,
      })
    );
  }, [dispatch, selectedGroupId]);

  useEffect(() => {
if (!copilot.error) {
return;
}

const error =
typeof copilot.error ===
"string"
? {
message:
copilot.error,
}
: copilot.error;

if (
error.errorType ===
"LIMIT_REACHED" &&
error.feature ===
"Copilot"
) {
Modal.confirm({
title:
"Add credits to continue",
content:
error.message ||
"You need at least 10 credits to use Copilot.",
okText:
"Add Credits",
cancelText:
"Close",
centered:
true,
onOk: () => {
navigate(
error.addCreditsPath ||
"/Dashboard/AccountSetting"
);
},
});
return;
}

antdMessage.error(
error.message ||
"Copilot request failed."
);
}, [
copilot.error,
navigate,
]);

  const updateAttachment = (attachmentId, patch = {}) => {
    setAttachments((prev) =>
      prev.map((attachment) => {
        if (attachment.id !== attachmentId) {
          return attachment;
        }

        return {
          ...attachment,
          ...patch,
          metadata: {
            ...(attachment.metadata || {}),
            ...(patch.metadata || {}),
          },
        };
      })
    );
  };

  const uploadAttachmentImmediately = async (attachment) => {
    try {
      const uploadedAsset = await uploadAssetService(
        attachment.file,
        selectedGroupId,
        (progressEvent) => {
          const total = progressEvent.total || attachment.file.size || 1;
          const progress = Math.round((progressEvent.loaded * 100) / total);

          updateAttachment(attachment.id, {
            progress,
          });
        }
      );

      const assetId = getUploadedAssetId(uploadedAsset);

      updateAttachment(attachment.id, {
        file: null,
        status: "completed",
        progress: 100,
        url: uploadedAsset.url,
        name: uploadedAsset.name || attachment.name || attachment.file.name,
        size: uploadedAsset.size || attachment.size || attachment.file.size,
        type:
          uploadedAsset.mimeType ||
          attachment.type ||
          attachment.file.type ||
          null,
        libraryAssetId: assetId,
        assetId,
        metadata: {
          ...(attachment.metadata || {}),
          source: "copilot_upload",
          uploadedToLibrary: true,
          uploadSource: "copilot",
          libraryAssetId: assetId,
          originalName: attachment.name || attachment.file.name || null,
          libraryAsset: uploadedAsset.libraryAsset || null,
        },
      });
    } catch (error) {
      updateAttachment(attachment.id, {
        status: "failed",
        progress: 0,
        error: error.message || "Upload failed.",
        metadata: {
          ...(attachment.metadata || {}),
          uploadedToLibrary: false,
          uploadSource: "copilot",
          uploadError: error.message || "Upload failed.",
        },
      });
    }
  };

  const handleFileSelect = (event) => {
const selectedFiles =
Array.from(
event.target.files ||
[]
);

event.target.value = "";

if (
selectedFiles.length === 0
) {
return;
}

if (!selectedGroupId) {
antdMessage.warning(
"Please select a brand before uploading files."
);
return;
}

const remainingSlots =
Math.max(
MAX_COPILOT_CHAT_ATTACHMENTS -
attachments.length,
0
);

if (remainingSlots === 0) {
antdMessage.warning(
`You can attach up to ${MAX_COPILOT_CHAT_ATTACHMENTS} files to one Copilot message.`
);
return;
}

const files =
selectedFiles.slice(
0,
remainingSlots
);

if (
selectedFiles.length >
remainingSlots
) {
antdMessage.warning(
`Only ${remainingSlots} more file${
remainingSlots === 1
? ""
: "s"
} can be added. Copilot supports up to ${MAX_COPILOT_CHAT_ATTACHMENTS} attachments per message.`
);
}

const nextAttachments =
files.map((file) => ({
id:
`file_${Date.now()}_${Math.random()
.toString(36)
.slice(2)}`,
name:
file.name,
size:
file.size,
type:
file.type,
file,
url: null,
status:
"uploading",
progress: 0,
error: null,
metadata: {
source:
"copilot_file_picker",
uploadedToLibrary:
false,
uploadSource:
"copilot",
originalName:
file.name,
},
}));

setAttachments((previous) => [
...previous,
...nextAttachments,
]);

nextAttachments.forEach(
(attachment) => {
uploadAttachmentImmediately(
attachment
);
}
);
};

  const removeAttachment = (attachmentId) => {
    setAttachments((prev) => prev.filter((item) => item.id !== attachmentId));
  };

  const refreshSessions = () => {
    if (!selectedGroupId) return;

    dispatch(
      fetchCopilotSessionsThunk({
        groupId: selectedGroupId,
        page: 1,
        limit: 20,
      })
    );
  };

  const submitPrompt = async (promptOverride = null) => {
    const cleanPrompt = String(promptOverride || inputValue || "").trim();

    if (!selectedGroupId) {
      antdMessage.warning("Please select a brand before using Copilot.");
      return;
    }

    if (hasUploadingAttachments) {
      antdMessage.warning("Please wait for file uploads to finish.");
      return;
    }

    const failedAttachments = attachments.filter(
      (attachment) => attachment.status === "failed"
    );

    if (failedAttachments.length > 0) {
      antdMessage.warning("Please remove failed uploads before sending.");
      return;
    }

    const uploadedAttachments = attachments
      .filter((attachment) => attachment.status === "completed" && attachment.url)
      .map(stripAttachmentForRequest);

    if (!cleanPrompt && uploadedAttachments.length === 0) {
      antdMessage.warning("Please enter a message or upload a file.");
      return;
    }

    setInputValue("");
    setAttachments([]);

    const result = await dispatch(
      sendCopilotChatThunk({
        groupId: selectedGroupId,
        sessionId: currentSessionId,
        message: cleanPrompt,
        attachments: uploadedAttachments,
        model: selectedModel,
        guardrails: {
          input: true,
          output: true,
        },
        metadata: {
          source: "dashboard_copilot_home",
          selectedGroup,
          attachmentUploadMode: "instant_media_library",
          uploadedAttachmentCount: uploadedAttachments.length,
        },
      })
    );

    if (
sendCopilotChatThunk.fulfilled.match(
result
)
) {
const writeActionResult =
result.payload
?.writeActionResult ||
null;

if (
writeActionResult?.operation ===
"brand_kit_update"
) {
const renamedBrand =
writeActionResult
?.renamedBrand === true;

const newBrandName =
writeActionResult
?.newBrandName ||
null;

const groupsResult =
await dispatch(
fetchGroups()
);

if (
renamedBrand &&
newBrandName
) {
const refreshedGroups =
Array.isArray(
groupsResult.payload
)
? groupsResult.payload
: [];

const renamedGroup =
refreshedGroups.find(
(group) =>
String(group.name) ===
String(newBrandName)
);

if (renamedGroup) {
dispatch(
setSelectedGroup(
newBrandName
)
);
}
}



const effectiveGroupName =
renamedBrand &&
newBrandName
? newBrandName
: selectedGroup;

if (effectiveGroupName) {
dispatch(
fetchGroupDetails(
effectiveGroupName
)
);

dispatch(
fetchAllProfileDetails(
effectiveGroupName
)
);
}
}
refreshSessions();
}

if (
sendCopilotChatThunk.rejected.match(
result
)
) {
setInputValue(cleanPrompt);
setAttachments(
uploadedAttachments.map(
(attachment, index) => ({
...attachment,
id:
attachment.id ||
`restored_${Date.now()}_${index}`,
status: "completed",
progress: 100,
})
)
);
}
};
const handleProfileSelection = async ({
message,
profiles,
}) => {
    if (
      !Array.isArray(profiles) ||
      profiles.length === 0
    ) {
      antdMessage.warning(
        "Select at least one connected profile."
      );
      return;
    }

    const profileDescriptions = profiles.map(
      (selectedProfile) => {
        const profileName = String(
          selectedProfile.profileName ||
            selectedProfile.profileId ||
            "Connected profile"
        ).trim();

        const platform = String(
          selectedProfile.platform || ""
        )
          .trim()
          .toUpperCase();

        const profileId = String(
          selectedProfile.profileId || ""
        ).trim();

        return `${profileName} (${platform}, profile ID: ${profileId})`;
      }
    );

    const clarificationResponse =
      `Use these connected profiles for the pending post action: ${profileDescriptions.join(
        "; "
      )}.`;

    const result = await dispatch(
      sendCopilotChatThunk({
        groupId: selectedGroupId,
        sessionId: currentSessionId,
        message: clarificationResponse,
        attachments: [],
        model: selectedModel,
        guardrails: {
          input: true,
          output: true,
        },
        metadata: {
          source:
            "copilot_connected_profile_selection",
          selectedGroup,
          clarificationMessageId:
            getMessageRenderId(
              message,
              null
            ),
          selectedProfiles: profiles.map(
            (selectedProfile) => ({
              platform:
                selectedProfile.platform,
              profileId:
                selectedProfile.profileId,
              connectionId:
                selectedProfile.connectionId ||
                null,
              profileName:
                selectedProfile.profileName ||
                null,
            })
          ),
        },
      })
    );

    if (
      sendCopilotChatThunk.fulfilled.match(
        result
      )
    ) {
      refreshSessions();
    }
  };

  const uploadPublishingRequirementMedia = async ({
    files = [],
    onProgress = null,
  } = {}) => {
    if (!selectedGroupId) {
      throw new Error(
        "Please select a brand before uploading media."
      );
    }

    const normalizedFiles = Array.isArray(files)
      ? files.filter(Boolean)
      : [];

    if (normalizedFiles.length === 0) {
      return [];
    }

    const uploadedAttachments = [];

    for (
      let index = 0;
      index < normalizedFiles.length;
      index += 1
    ) {
      const file = normalizedFiles[index];

      const uploadedAsset =
        await uploadAssetService(
          file,
          selectedGroupId,
          (progressEvent) => {
            const total =
              progressEvent.total ||
              file.size ||
              1;

            const progress = Math.round(
              (progressEvent.loaded * 100) /
                total
            );

            if (
              typeof onProgress ===
              "function"
            ) {
              onProgress({
                index,
                file,
                progress,
              });
            }
          }
        );

      const assetId =
        getUploadedAssetId(
          uploadedAsset
        );

      uploadedAttachments.push({
        id: `requirement_media_${Date.now()}_${index}_${Math.random()
          .toString(36)
          .slice(2)}`,
        name:
          uploadedAsset.name ||
          file.name ||
          "Uploaded media",
        size: Number(
          uploadedAsset.size ||
            file.size ||
            0
        ),
        type:
          uploadedAsset.mimeType ||
          file.type ||
          null,
        mediaType:
          uploadedAsset.type ||
          (
            String(
              uploadedAsset.mimeType ||
                file.type ||
                ""
            ).startsWith("video/")
              ? "video"
              : String(
                  uploadedAsset.mimeType ||
                    file.type ||
                    ""
                ).startsWith("image/")
              ? "image"
              : "other"
          ),
        url:
          uploadedAsset.url ||
          null,
        libraryAssetId:
          assetId,
        assetId,
        metadata: {
          source:
            "copilot_publishing_requirements",
          uploadedToLibrary: true,
          uploadSource:
            "copilot",
          libraryAssetId:
            assetId,
          originalName:
            file.name || null,
          libraryAsset:
            uploadedAsset.libraryAsset ||
            null,
        },
      });
    }

    return uploadedAttachments;
  };

  const handlePublishingRequirements = async ({
    message,
    profiles,
    platformOptions,
    attachments: requirementAttachments = [],
  }) => {
    if (
      !Array.isArray(profiles) ||
      profiles.length === 0
    ) {
      antdMessage.warning(
        "Keep at least one connected profile."
      );
      return;
    }

    const profileDescriptions =
      profiles.map((selectedProfile) => {
        return `${selectedProfile.profileName} (${selectedProfile.platform}, profile ID: ${selectedProfile.profileId})`;
      });

    const normalizedRequirementAttachments =
      Array.isArray(
        requirementAttachments
      )
        ? requirementAttachments
            .filter(
              (attachment) =>
                attachment?.url
            )
            .map(
              stripAttachmentForRequest
            )
        : [];

    const mediaDescription =
      normalizedRequirementAttachments
        .length > 0
        ? normalizedRequirementAttachments
            .map((attachment) => {
              return `${attachment.name || "media"} (${attachment.mediaType || attachment.type || "media"}, asset ID: ${attachment.assetId || attachment.libraryAssetId || "unavailable"})`;
            })
            .join("; ")
        : "No new media was attached.";

    const clarificationResponse = [
      `Continue the pending post action using exactly these connected profiles: ${profileDescriptions.join(
        "; "
      )}.`,
      `Apply these exact platform options: ${JSON.stringify(
        platformOptions || {}
      )}.`,
      `Use these uploaded media items: ${mediaDescription}`,
    ].join(" ");

    const result = await dispatch(
      sendCopilotChatThunk({
        groupId:
          selectedGroupId,
        sessionId:
          currentSessionId,
        message:
          clarificationResponse,
        attachments:
          normalizedRequirementAttachments,
        model: selectedModel,
        guardrails: {
          input: true,
          output: true,
        },
        metadata: {
          source:
            "copilot_publishing_requirements",
          selectedGroup,
          clarificationMessageId:
            getMessageRenderId(
              message,
              null
            ),
          selectedProfiles:
            profiles,
          platformOptions:
            platformOptions || {},
          requirementAttachments:
            normalizedRequirementAttachments.map(
              (attachment) => ({
                name:
                  attachment.name ||
                  null,
                type:
                  attachment.type ||
                  null,
                mediaType:
                  attachment.mediaType ||
                  null,
                url:
                  attachment.url ||
                  null,
                assetId:
                  attachment.assetId ||
                  attachment.libraryAssetId ||
                  null,
              })
            ),
          uploadedAttachmentCount:
            normalizedRequirementAttachments.length,
        },
      })
    );

    if (
      sendCopilotChatThunk.fulfilled.match(
        result
      )
    ) {
      refreshSessions();
      return;
    }

    throw new Error(
      result.payload ||
        "Failed to continue the pending post action."
    );
  };


const handleReadResultAction = async ({
message,
action,
fieldKeys = [],
}) => {
const sourceMessageId =
getMessageRenderId(
message,
null
);

if (
!currentSessionId ||
!sourceMessageId ||
!action?.id
) {
throw new Error(
"The read-result action reference is incomplete."
);
}

const result =
await dispatch(
startCopilotReadResultActionThunk({
sessionId:
currentSessionId,
sourceMessageId,
actionId:
action.id,
fieldKeys:
Array.isArray(fieldKeys)
? fieldKeys
: [],
})
);

if (
startCopilotReadResultActionThunk
.fulfilled.match(result)
) {
refreshSessions();
return result.payload;
}

throw new Error(
result.payload ||
"Failed to start the requested management action."
);
};

  const handleActionDraftSubmit = async ({
actionDraftId,
revision,
patch,
metadata:
submissionMetadata = {},
}) => {
    if (
      !actionDraftId ||
      !currentSessionId
    ) {
      throw new Error(
        "The action draft reference is incomplete."
      );
    }

    const result = await dispatch(
      patchCopilotActionDraftThunk({
        sessionId:
          currentSessionId,
        actionDraftId,
        expectedRevision:
          revision,
        patch,
        metadata: {
source:
"copilot_action_form",
selectedGroup,
deviceTimezone:
Intl.DateTimeFormat()
.resolvedOptions()
.timeZone ||
"UTC",
...(submissionMetadata ||
{}),
},
      })
    );

    if (
      patchCopilotActionDraftThunk
        .fulfilled.match(result)
    ) {
      refreshSessions();
      return result.payload;
    }

    throw new Error(
      result.payload ||
        "Failed to update the action."
    );
  };

  const handleActionDraftCancel = async ({
    actionDraftId,
  }) => {
    if (
      !actionDraftId ||
      !currentSessionId
    ) {
      throw new Error(
        "The action draft reference is incomplete."
      );
    }

    const result = await dispatch(
      cancelCopilotActionDraftThunk({
        sessionId:
          currentSessionId,
        actionDraftId,
        reason:
          "action_form_cancelled",
      })
    );

    if (
      cancelCopilotActionDraftThunk
        .fulfilled.match(result)
    ) {
      antdMessage.success(
        "Action cancelled."
      );

      refreshSessions();
      return result.payload;
    }

    throw new Error(
      result.payload ||
        "Failed to cancel the action."
    );
  };

  const handleActionDraftMediaUpload = async ({
    files = [],
    onProgress = null,
  } = {}) => {
    return uploadPublishingRequirementMedia({
      files,
      onProgress,
    });
  };

  const handleConfirmAction = async (
    pendingAction
  ) => {
    const actionId =
      pendingAction?._id ||
      pendingAction?.id ||
      null;

    const sessionId =
      pendingAction?.sessionId ||
      currentSessionId;

    const confirmationToken =
      pendingAction?.confirmationToken ||
      null;

    if (
      !actionId ||
      !sessionId ||
      !confirmationToken
    ) {
      antdMessage.error(
        "This action confirmation is incomplete. Please ask Copilot to prepare it again."
      );
      return;
    }

    const result = await dispatch(
      confirmCopilotActionThunk({
        sessionId,
        actionId,
        confirmationToken,
      })
    );

    if (
      confirmCopilotActionThunk.fulfilled.match(
        result
      )
    ) {
      const operation =
        result.payload?.result
          ?.operation ||
        null;

      const successMessage =
operation ===
"app_generate_carousel"
? `Carousel generation queued${
Number(
result.payload?.result
?.cardCount ||
0
) > 0
? ` for ${
result.payload.result
.cardCount
} card${
Number(
result.payload.result
.cardCount
) === 1
? ""
: "s"
}`
: ""
}${
result.payload?.result
?.platform
? ` · ${
result.payload.result
.platform
}`
: ""
}${
result.payload?.result
?.transactionId
? ` · Transaction ${
result.payload.result
.transactionId
}`
: ""
}${
Number(
result.payload?.result
?.creditsCharged ||
0
) > 0
? ` · ${
result.payload.result
.creditsCharged
} credit${
Number(
result.payload.result
.creditsCharged
) === 1
? ""
: "s"
}`
: ""
}.`
: operation ===
"app_generate_moodboard"
? `Moodboard generation queued${
Number(
result.payload?.result
?.imageCount ||
0
) > 0
? ` with ${
result.payload.result
.imageCount
} reference image${
Number(
result.payload.result
.imageCount
) === 1
? ""
: "s"
}`
: ""
}${
result.payload?.result
?.transactionId
? ` · Transaction ${
result.payload.result
.transactionId
}`
: ""
}${
Number(
result.payload?.result
?.creditsCharged ||
0
) > 0
? ` · ${
result.payload.result
.creditsCharged
} credit${
Number(
result.payload.result
.creditsCharged
) === 1
? ""
: "s"
}`
: ""
}.`
: operation ===
"app_upscale_media"
? `${
result.payload?.result
?.mediaKind ===
"video"
? "Video"
: "Image"
} upscaling queued${
Number(
result.payload?.result
?.scale ||
0
) > 0
? ` at ${
result.payload.result
.scale
}x`
: ""
}${
result.payload?.result
?.transactionId
? ` · Transaction ${
result.payload.result
.transactionId
}`
: ""
}${
Number(
result.payload?.result
?.creditsCharged ||
0
) > 0
? ` · ${
result.payload.result
.creditsCharged
} credit${
Number(
result.payload.result
.creditsCharged
) === 1
? ""
: "s"
}`
: ""
}.`
: operation ===
"app_generate_image"
? `Image generation queued${
result.payload?.result
?.transactionId
? ` · Transaction ${
result.payload.result
.transactionId
}`
: ""
}${
Number(
result.payload?.result
?.creditsCharged ||
0
) > 0
? ` · ${
result.payload.result
.creditsCharged
} credit${
Number(
result.payload.result
.creditsCharged
) === 1
? ""
: "s"
}`
: ""
}.`
: operation ===
"app_resize_image"
? `Image resizing queued${
result.payload?.result
?.transactionId
? ` · Transaction ${
result.payload.result
.transactionId
}`
: ""
}${
Number(
result.payload?.result
?.creditsCharged ||
0
) > 0
? ` · ${
result.payload.result
.creditsCharged
} credit${
Number(
result.payload.result
.creditsCharged
) === 1
? ""
: "s"
}`
: ""
}.`
: operation ===
"app_extract_color_palette"
? `Color palette extraction queued${
result.payload?.result
?.transactionId
? ` · Transaction ${
result.payload.result
.transactionId
}`
: ""
}${
Number(
result.payload?.result
?.creditsCharged ||
0
) > 0
? ` · ${
result.payload.result
.creditsCharged
} credit${
Number(
result.payload.result
.creditsCharged
) === 1
? ""
: "s"
}`
: ""
}.`
: operation ===
"app_color_grade_image"
? `Image color grading queued${
result.payload?.result
?.transactionId
? ` · Transaction ${
result.payload.result
.transactionId
}`
: ""
}${
Number(
result.payload?.result
?.creditsCharged ||
0
) > 0
? ` · ${
result.payload.result
.creditsCharged
} credit${
Number(
result.payload.result
.creditsCharged
) === 1
? ""
: "s"
}`
: ""
}.`
: operation ===
"create_draft_batch"
? `${
Number(
result.payload?.result
?.successfulDraftCount ||
0
)
} draft proposal${
Number(
result.payload?.result
?.successfulDraftCount ||
0
) === 1
? ""
: "s"
} created successfully${
Number(
result.payload?.result
?.failedDraftCount ||
0
) > 0
? ` · ${
result.payload.result
.failedDraftCount
} failed`
: ""
}.`
: operation ===
"asset_rename"
? "Asset renamed successfully."
: operation ===
"asset_delete"
? `${
Number(
result.payload
?.result
?.deletedCount ||
0
)
} asset${
Number(
result.payload
?.result
?.deletedCount ||
0
) === 1
? ""
: "s"
} deleted successfully.`
: operation ===
"account_disconnect"
? `${
Number(
result.payload
?.result
?.disconnectedCount ||
0
)
} connected profile${
Number(
result.payload
?.result
?.disconnectedCount ||
0
) === 1
? ""
: "s"
} disconnected successfully.`
: operation ===
"brand_kit_update"
? `${
Number(
result.payload
?.result
?.updatedCount ||
0
)
} Brand Kit value${
Number(
result.payload
?.result
?.updatedCount ||
0
) === 1
? ""
: "s"
} updated successfully.`
: "Action completed successfully.";

      antdMessage.success(
        successMessage
      );

      if (
operation ===
"brand_kit_update"
) {
const renamedBrand =
result.payload?.result
?.renamedBrand ===
true;

const newBrandName =
result.payload?.result
?.newBrandName ||
null;

const groupsResult =
await dispatch(
fetchGroups()
);

if (
renamedBrand &&
newBrandName
) {
const refreshedGroups =
Array.isArray(
groupsResult.payload
)
? groupsResult.payload
: [];

const renamedGroup =
refreshedGroups.find(
(group) =>
String(group.name) ===
String(newBrandName)
);

if (renamedGroup) {
dispatch(
setSelectedGroup(
newBrandName
)
);
}
}

const effectiveGroupName =
renamedBrand &&
newBrandName
? newBrandName
: selectedGroup;

if (effectiveGroupName) {
dispatch(
fetchGroupDetails(
effectiveGroupName
)
);
}

dispatch(
fetchAllProfileDetails(
effectiveGroupName
)
);
}


      if (
  operation ===
  "account_disconnect"
) {
  dispatch(fetchGroups());

  if (selectedGroup) {
    dispatch(
      fetchGroupDetails(
        selectedGroup
      )
    );
  }

  dispatch(
    fetchAllProfileDetails(
      selectedGroup
    )
  );
}



      refreshSessions();
    }
  };


  const handleEditAction = async (
pendingAction
) => {
const actionId =
pendingAction?._id ||
pendingAction?.id ||
null;

const sessionId =
pendingAction?.sessionId ||
currentSessionId;

if (
!actionId ||
!sessionId
) {
antdMessage.error(
"This action cannot be edited because its reference is incomplete."
);
return;
}

const result =
await dispatch(
editCopilotActionThunk({
sessionId,
actionId,
})
);

if (
  editCopilotActionThunk
    .fulfilled.match(result)
) {
  antdMessage.success(
    "Action reopened for editing."
  );

  refreshSessions();
}
};


  const handleCancelAction = async (
    pendingAction
  ) => {
    const actionId =
      pendingAction?._id ||
      pendingAction?.id ||
      null;

    const sessionId =
      pendingAction?.sessionId ||
      currentSessionId;

    if (!actionId || !sessionId) {
      antdMessage.error(
        "This action cannot be cancelled because its reference is incomplete."
      );
      return;
    }

    const result = await dispatch(
      cancelCopilotActionThunk({
        sessionId,
        actionId,
        reason:
          "confirmation_button_cancelled",
      })
    );

    if (
      cancelCopilotActionThunk.fulfilled.match(
        result
      )
    ) {
      antdMessage.success(
        "Action cancelled."
      );
      refreshSessions();
    }
  };

  const handleCancelGoal = async (
    goal
  ) => {
    const goalId =
      goal?._id ||
      goal?.id ||
      null;

    const sessionId =
      goal?.sessionId ||
      currentSessionId;

    if (!goalId || !sessionId) {
      antdMessage.error(
        "This goal cannot be cancelled because its reference is incomplete."
      );
      return;
    }

    const result =
      await dispatch(
        cancelCopilotGoalThunk({
          sessionId,
          goalId,
          reason:
            "goal_card_cancelled",
        })
      );

    if (
      cancelCopilotGoalThunk
        .fulfilled.match(result)
    ) {
      antdMessage.success(
        "Goal cancelled."
      );
      refreshSessions();
    }
  };

  const handleAccountConnectRequest = ({
  interaction,
  selectedPlatform = null,
} = {}) => {
  const getPlatformValue = (
    item
  ) => {
    return typeof item ===
      "string"
      ? item
      : item?.platform;
  };

  const displayedPlatforms =
    Array.isArray(
      interaction?.platforms
    )
      ? interaction.platforms
          .map(getPlatformValue)
          .filter(Boolean)
      : [];

  const requestedPlatforms =
    Array.isArray(
      interaction?.requestedPlatforms
    )
      ? interaction.requestedPlatforms
          .map(getPlatformValue)
          .filter(Boolean)
      : displayedPlatforms;

  const platforms =
    selectedPlatform
      ? [selectedPlatform]
      : displayedPlatforms;

  setConnectInteraction({
    platforms,
    requestedPlatforms,
    sessionId:
      interaction?.sessionId ||
      currentSessionId ||
      null,
  });
};

const handleConnectModalClose =
  () => {
    setConnectInteraction(null);

    dispatch(fetchGroups());

    if (selectedGroup) {
      dispatch(
        fetchGroupDetails(
          selectedGroup
        )
      );
    }

    dispatch(
      fetchAllProfileDetails(
        selectedGroup
      )
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitPrompt();
    }
  };

  const handleSkillPrompt = (prompt) => {
    setShowSkills(false);
    setInputValue(prompt);
    window.setTimeout(() => textareaRef.current?.focus?.(), 0);
  };

  const handleSelectSession = async (session) => {
    const sessionId = getSessionId(session);
    if (!sessionId) return;

    await dispatch(fetchCopilotMessagesThunk(sessionId));
    setHistoryOpen(false);
  };

  const handleNewChat = () => {
    dispatch(clearCopilotConversation());
    setHistoryOpen(false);
    window.setTimeout(() => textareaRef.current?.focus?.(), 0);
  };

  const handleRenameSession = async (session, title) => {
  const sessionId = getSessionId(session);

  if (!sessionId) return;

  await dispatch(
    renameCopilotSessionThunk({
      sessionId,
      title,
    })
  );
  };

  const handleDeleteSession = async (session) => {
    const sessionId = getSessionId(session);

    if (!sessionId) return;

    await dispatch(deleteCopilotSessionThunk(sessionId));

    if (String(currentSessionId || "") === String(sessionId)) {
      dispatch(clearCopilotConversation());
      window.setTimeout(() => textareaRef.current?.focus?.(), 0);
    }
  };

  const plusMenuItems = [
    {
      key: "upload",
      label: (
        <span className="flex items-center gap-2 font-semibold">
          <i className="fa-solid fa-paperclip"></i>
          Upload file
        </span>
      ),
    },
  ];

  const handlePlusMenuClick = ({ key }) => {
    if (key === "upload") {
      fileInputRef.current?.click();
      return;
    }
  };

  return (
    <>
            <style>
        {`
          .copilot-home-shell,
.copilot-chat-scroll,
.copilot-no-scrollbar {
scrollbar-width: none !important;
-ms-overflow-style: none !important;
}

.copilot-home-shell::-webkit-scrollbar,
.copilot-chat-scroll::-webkit-scrollbar,
.copilot-no-scrollbar::-webkit-scrollbar {
display: none !important;
width: 0 !important;
height: 0 !important;
}

          .copilot-model-select.ant-select-single {
            height: 34px;
          }

          .copilot-model-select .ant-select-selector {
            min-height: 34px !important;
            border: 1px solid rgba(255,255,255,0.09) !important;
            border-radius: 10px !important;
            background: rgba(255,255,255,0.055) !important;
            box-shadow: none !important;
            padding: 0 30px 0 11px !important;
            transition:
              background 160ms ease,
              border-color 160ms ease !important;
          }

          .copilot-model-select:hover .ant-select-selector,
          .copilot-model-select.ant-select-focused .ant-select-selector {
            border-color: rgba(255,255,255,0.17) !important;
            background: rgba(255,255,255,0.085) !important;
            box-shadow: none !important;
          }

          .copilot-model-select .ant-select-selection-item {
            color: rgba(255,255,255,0.82) !important;
            font-size: 12px !important;
            font-weight: 700 !important;
            line-height: 32px !important;
          }

          .copilot-model-select .ant-select-arrow {
            color: rgba(255,255,255,0.48) !important;
            right: 11px !important;
          }

          .copilot-model-popup .ant-select-item {
            min-height: 38px !important;
            border-radius: 8px !important;
            color: rgba(255,255,255,0.72) !important;
            font-size: 13px !important;
            font-weight: 600 !important;
          }

          .copilot-model-popup .ant-select-item-option-active {
            background: rgba(255,255,255,0.07) !important;
          }

          .copilot-model-popup .ant-select-item-option-selected {
            background: rgba(139,92,246,0.16) !important;
            color: #ffffff !important;
          }

          .copilot-plus-dropdown .ant-dropdown-menu,
          .copilot-model-popup {
            border: 1px solid rgba(255,255,255,0.09) !important;
            border-radius: 12px !important;
            background: #1b1d21 !important;
            padding: 6px !important;
            box-shadow: 0 18px 50px rgba(0,0,0,0.5) !important;
          }

          .copilot-plus-dropdown .ant-dropdown-menu-item {
            border-radius: 8px !important;
            color: rgba(255,255,255,0.76) !important;
            padding: 9px 10px !important;
          }

          .copilot-plus-dropdown .ant-dropdown-menu-item:hover {
            background: rgba(255,255,255,0.07) !important;
            color: #ffffff !important;
          }
        `}
      </style>

        <div className="copilot-home-shell relative flex h-full min-h-0 overflow-hidden bg-[#121418] text-white">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_12%,rgba(255,255,255,0.045),transparent_30%),radial-gradient(circle_at_12%_88%,rgba(124,58,237,0.1),transparent_25%),radial-gradient(circle_at_90%_82%,rgba(59,130,246,0.055),transparent_24%)]"></div>

        <div className="relative z-10 flex h-full min-h-0 w-full flex-col px-5 py-4">
          <header className="flex flex-shrink-0 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <div className="hidden rounded-lg bg-white/[0.035] px-3 py-2 text-xs font-medium text-white/40 sm:block">
                Working in:{" "}
                <span className="font-semibold text-white/85">
                  {selectedGroup || "No brand selected"}
                </span>
              </div>

              {selectedGroupDetails?.connected_accounts?.length > 0 && (
                <div className="hidden rounded-lg bg-white/[0.035] px-3 py-2 text-xs font-semibold text-white/35 lg:block">
                  {selectedGroupDetails.connected_accounts.length} connected
                  profile
                  {selectedGroupDetails.connected_accounts.length === 1
                    ? ""
                    : "s"}
                </div>
              )}

              {currentSessionId && (
                <div className="hidden rounded-lg bg-white/[0.03] px-3 py-2 text-xs font-semibold text-white/28 xl:block">
                  Session {String(currentSessionId).slice(-8)}
                </div>
              )}
            </div>

            <div className="flex flex-shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-white/[0.05] px-3.5 text-sm font-semibold text-white/65 transition hover:bg-white/[0.09] hover:text-white"
              >
                <i className="fa-solid fa-clock-rotate-left"></i>
                History
              </button>

              {hasConversation && (
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-white px-3.5 text-sm font-semibold text-black transition hover:bg-white/90"
                >
                  <i className="fa-solid fa-plus text-xs"></i>
                  <span className="hidden sm:inline">New Chat</span>
                </button>
              )}
            </div>
          </header>

          <main
            className={`flex min-h-0 flex-1 flex-col items-center ${
              hasConversation ? "justify-start" : "justify-center"
            }`}
          >
            <div
              className={`flex min-h-0 w-full max-w-4xl flex-col ${
                hasConversation ? "flex-1 pt-3" : ""
              }`}
            >
              {!hasConversation && (
                <div className="mb-6 text-center">
                  <h1 className="mb-0 text-3xl font-black tracking-[-0.045em] text-white sm:text-5xl">
                    {greetingText}
                  </h1>
                </div>
              )}

              {hasConversation && (
<div
ref={
chatScrollRef
}
onScroll={
updateChatScrollPosition
}
className="copilot-chat-scroll mx-auto min-h-0 w-full max-w-3xl flex-1 space-y-4 overflow-y-auto px-3 pb-8 pt-5 sm:px-5"
>
                  {renderedMessages.map((messageItem, index) => (
                    <React.Fragment
                      key={getMessageRenderId(messageItem, index)}
                    >
                      {messageItem.role !==
                        "tool" &&
                        messageItem.promptGoal && (
                          <CopilotGoalCard
                            goal={
                              messageItem.promptGoal
                            }
                            loading={
                              isGoalRunning
                            }
                            onCancel={
                              handleCancelGoal
                            }
                          />
                        )}

                    <CopilotMessageBubble
                      message={messageItem}
                      promptGoal={
                        messageItem.promptGoal ||
                        null
                      }
                      onCancelGoal={
                        handleCancelGoal
                      }
                      goalLoading={
                        isGoalRunning
                      }
onReadResultAction={handleReadResultAction}
onConfirmAction={handleConfirmAction}
onCancelAction={handleCancelAction}
onEditAction={handleEditAction}
onSubmitProfileSelection={handleProfileSelection}
                      onSubmitPublishingRequirements={
  handlePublishingRequirements
}
onConnectAccount={
  handleAccountConnectRequest
}
                      onSubmitActionDraft={
                        handleActionDraftSubmit
                      }
                      onCancelActionDraft={
                        handleActionDraftCancel
                      }
                      loadRemoteOptions={
async (dataSource) => {
if (
dataSource?.type ===
"pinterest.boards"
) {
return getBoardApi(
dataSource.profileId
);
}

if (
dataSource?.type ===
"youtube.playlists"
) {
return getYoutubePlaylistsApi(
dataSource.profileId
);
}

if (
dataSource?.type ===
"image_generate.models"
) {
try {
const models =
await fetchImageGenerateModels();

return {
error: false,
data:
models.map((item) => ({
model:
item.model,
label:
item.label ||
item.model,
})),
};
} catch (error) {
return {
error: true,
data: [],
message:
error.message ||
"Failed to load Image Generator models.",
};
}
}

if (
dataSource?.type ===
"brand_kit.styles"
) {
if (!selectedGroupId) {
return {
error: true,
data: [],
message:
"Select a brand before loading Brand Styles.",
};
}
try {
const brandKit =
await getOnboardingForGroup(
selectedGroupId
);
const styles =
brandKit
?.Visual_Style_Guides &&
typeof brandKit
.Visual_Style_Guides ===
"object" &&
!Array.isArray(
brandKit
.Visual_Style_Guides
)
? brandKit
.Visual_Style_Guides
: {};
return {
error: false,
data:
Object.entries(
styles
)
.map(
([
key,
description,
]) => {
const normalizedKey =
String(
key || ""
).trim();
if (!normalizedKey) {
return null;
}
return {
key:
normalizedKey,
label:
normalizedKey
.replace(
/_/g,
" "
)
.replace(
/\b\w/g,
(character) =>
character.toUpperCase()
),
description:
String(
description || ""
).trim(),
};
}
)
.filter(Boolean),
};
} catch (error) {
return {
error: true,
data: [],
message:
error.message ||
"Failed to load Brand Styles.",
};
}
}
if (
dataSource?.type ===
"countries"
) {
return {
error: false,
data:
countryList.map(
(countryName) => ({
id:
countryName,
name:
countryName,
})
),
};
}

if (
dataSource?.type ===
"google_fonts"
) {
try {
const fonts =
await fetchGoogleFonts();

return {
error: false,
data:
fonts.map(
(fontName) => ({
id:
fontName,
name:
fontName,
})
),
};
} catch (error) {
return {
error: true,
data: [],
message:
error.message ||
"Failed to load Google Fonts.",
};
}
}

return {
error: true,
data: [],
message:
"Unsupported remote data source.",
};
}
}
                      uploadActionDraftMedia={
                        handleActionDraftMediaUpload
                      }
                      loadPinterestBoards={
                        getBoardApi
                      }
                      uploadPublishingMedia={
                        uploadPublishingRequirementMedia
                      }
                      profileSelectionEnabled={
                        String(
                          getMessageRenderId(
                            messageItem,
                            ""
                          )
                        ) ===
                        String(
                          latestClarificationMessageId ||
                            ""
                        )
                      }
                      activeActionId={copilot.activeActionId}
                      actionStatus={copilot.actionStatus}
                      actionDraftStatus={
                        copilot.actionDraftStatus
                      }
                                            chatStatus={copilot.chatStatus}
                    />
                    </React.Fragment>
                  ))}

                  {isBusy && (
                    <div className="flex items-center gap-3 text-sm font-bold text-white/50">
                      <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.08] text-white">
                        <i className="fa-solid fa-wand-magic-sparkles text-[11px]"></i>
                      </div>
                      <Spin size="small" />
                      <span>
                        {hasUploadingAttachments
                          ? "Uploading file..."
                          : isActionRunning
                          ? "Executing confirmed action..."
                          : "Copilot is thinking..."}
                      </span>
                    </div>
                  )}
</div>
)}

{hasConversation &&
showScrollToBottom && (
<button
type="button"
onClick={() =>
scrollChatToBottom(
"smooth"
)
}
aria-label="Scroll to the latest message"
title="Scroll to latest message"
className="absolute bottom-24 right-3 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.1] bg-[#24262c] text-white/70 shadow-[0_12px_35px_rgba(0,0,0,0.45)] transition hover:scale-105 hover:bg-[#2c2f36] hover:text-white sm:right-6"
>
<i className="fa-solid fa-arrow-down text-xs"></i>
</button>
)}

              <div
                className={`mx-auto w-full max-w-3xl flex-shrink-0 rounded-2xl bg-[#1b1d21] px-2.5 py-2 shadow-[0_18px_55px_rgba(0,0,0,0.32)] ring-1 ring-white/[0.045] ${
                  hasConversation ? "mb-2" : ""
                }`}
              >
                {attachments.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2 px-2">
                    {attachments.map((attachment) => (
                      <span
                        key={attachment.id}
                        title={attachment.error || attachment.name}
                        className={`inline-flex max-w-[260px] items-center gap-2 rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                          attachment.status === "failed"
                            ? "bg-red-500/15 text-red-100"
                            : attachment.status === "completed"
                            ? "bg-emerald-500/15 text-emerald-100"
                            : "bg-white/[0.08] text-white/65"
                        }`}
                      >
                        <i className="fa-solid fa-paperclip"></i>
                        <span className="truncate">{attachment.name}</span>

                        {attachment.status === "uploading" && (
                          <span className="rounded-full bg-white/[0.08] px-1.5 py-0.5 text-[10px] text-white/45">
                            {attachment.progress || 0}%
                          </span>
                        )}

                        {attachment.status === "completed" && (
                          <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[10px] text-emerald-100">
                            uploaded
                          </span>
                        )}

                        {attachment.status === "failed" && (
                          <span className="rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] text-red-100">
                            failed
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={() => removeAttachment(attachment.id)}
                          className="text-white/40 hover:text-white"
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    multiple
                    onChange={handleFileSelect}
                    accept="image/*,video/*,audio/*,.pdf,.docx,.pptx,.txt"
                  />

                  <Dropdown
                    trigger={["click"]}
                    placement="bottomLeft"
                    menu={{
                      items: plusMenuItems,
                      onClick: handlePlusMenuClick,
                    }}
                    overlayClassName="copilot-plus-dropdown"
                  >
                    <button
                      type="button"
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-white/45 transition hover:bg-white/[0.07] hover:text-white"
                      aria-label="Open upload menu"
                    >
                      <i className="fa-solid fa-plus text-base"></i>
                    </button>
                  </Dropdown>

                  <textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={(event) => setInputValue(event.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    placeholder="Ask anything"
                    className="copilot-chat-scroll max-h-28 min-h-[36px] flex-1 resize-none border-0 bg-transparent px-1 py-1.5 text-[15px] font-medium leading-6 text-white outline-none placeholder:text-white/30"
                  />

                  <div className="hidden flex-shrink-0 items-center sm:flex">
                    <Select
                      value={selectedModel}
                      onChange={setSelectedModel}
                      options={modelOptions}
                      popupMatchSelectWidth={false}
                      popupClassName="copilot-model-popup"
                      className="copilot-model-select min-w-[118px]"
                      suffixIcon={
                        <i className="fa-solid fa-chevron-down text-[9px]"></i>
                      }
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => submitPrompt()}
                    disabled={isBusy}
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-white text-black transition hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label="Send message"
                  >
                    {isBusy ? (
                      <Spin size="small" />
                    ) : (
                      <i className="fa-solid fa-arrow-up text-base"></i>
                    )}
                  </button>
                </div>
              </div>

              <div className="mx-auto mt-3 flex max-w-3xl justify-center sm:hidden">
                <Select
                  value={selectedModel}
                  onChange={setSelectedModel}
                  options={modelOptions}
                  popupMatchSelectWidth={false}
                  popupClassName="copilot-model-popup"
                  className="copilot-model-select min-w-[132px]"
                  suffixIcon={
                    <i className="fa-solid fa-chevron-down text-[9px]"></i>
                  }
                />
              </div>

              <div className="flex-shrink-0">
                <SourceReferenceBar knowledge={copilot.lastKnowledge} />
              </div>

              {showSkills && (
                <div className="mx-auto mt-3 flex max-w-3xl flex-shrink-0 flex-wrap items-center justify-center gap-2">
                  {skillPrompts.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => handleSkillPrompt(item.prompt)}
                      className="rounded-lg bg-white/[0.045] px-3 py-1.5 text-xs font-semibold text-white/50 transition hover:bg-white/[0.08] hover:text-white"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}

              {!hasConversation && (
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                  {quickPrompts.map((item) => (
                    <button
                      key={item.title}
                      type="button"
                      onClick={() => submitPrompt(item.prompt)}
                      disabled={isBusy}
                      className="inline-flex items-center gap-2 rounded-xl bg-white/[0.045] px-4 py-2.5 text-sm font-semibold text-white/60 transition hover:bg-white/[0.085] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <i className={item.icon}></i>
                      {item.title}
                    </button>
                  ))}
                </div>
              )}

              
            </div>
          </main>
        </div>
      </div>

      <CopilotHistoryDrawer
  open={historyOpen}
  onClose={() => setHistoryOpen(false)}
  sessions={copilot.sessions}
  loading={historyLoading}
  selectedSessionId={currentSessionId}
  onSelectSession={handleSelectSession}
  onNewChat={handleNewChat}
  onRefresh={refreshSessions}
  onRenameSession={handleRenameSession}
  onDeleteSession={handleDeleteSession}
/>

<ConnectProfileModal
  isOpen={Boolean(connectInteraction)}
  onClose={handleConnectModalClose}
  allowedPlatforms={
    connectInteraction?.platforms ||
    null
  }
  connectionContext={{
    source: "copilot",
sessionId:
  connectInteraction?.sessionId ||
  currentSessionId ||
  null,
requestedPlatforms:
  connectInteraction
    ?.requestedPlatforms ||
  connectInteraction?.platforms ||
  [],
returnPath:
  connectInteraction?.sessionId ||
  currentSessionId
    ? `/Dashboard?copilotSession=${encodeURIComponent(
        connectInteraction?.sessionId ||
          currentSessionId
      )}`
    : "/Dashboard",
  }}
/>
</>
);
}